import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import EmberObject, { action, computed } from '@ember/object';
import { task } from 'ember-concurrency';

const CLASS_NAME = 'SH-c-admin-users';

const FILTERS = {
  TYPE: 'type',
  ID_SLUG_EMAIL: 'idSlugEmail',
  NAME: 'name',
  COUNTRY: 'country',
  DELETION_REQUEST_ONLY: 'deletionRequestOnly',
  HAS_APPLIED: 'hasApplied',
  HAS_COMPLETED: 'hasCompleted',
  IS_INVITED: 'isInvited',
  IS_SELECT: 'isSelect',
  HAS_PREFERRED_PAYOUT_ACCOUNT: 'hasPreferredPayoutAccount',
  MINIMUM_RATING: 'minimumRating',
  HAS_SERVICE: 'hasService',
  VERIFIED: 'verified',
  VETTING: 'vetting',
  HAS_LANGUAGE: 'hasLanguage',
  COMPANY_ID: 'companyId',
  HAS_ASSIGNMENT: 'hasAssignment',
  BUDGET_EXPERIENCE: 'budgetExperience',
};

const INCLUDES = [
  'company',
  'devices',
  'image',
  'company-seller-connections.company:in_network_only',
  'location',
  'last-location',
  'preferred-payout-account',
  'profile',
  'profile.languages',
  'services:sort(most-proof)',
  'user-statistics',
];

const SORTS = {
  BEST_MATCH: 'best_match',
  UPDATED_AT: 'updated_at',
  CREATED_AT: 'created_at',
};

const SORT_NAMES = {
  [SORTS.BEST_MATCH]: 'Best Match',
  [SORTS.UPDATED_AT]: 'Last updated',
  [SORTS.CREATED_AT]: 'Created',
};

const SORT_ORDERS = {
  ASC: 'asc',
  DESC: 'desc',
};

export const ADMIN_USER_CONTROLLER = {
  FILTERS,
  FILTER_VALUES: Object.values(FILTERS),
  INCLUDES,
  SORTS,
  SORT_NAMES,
  SORT_ORDERS,
};

class AdminUsersController extends Controller {
  @service constants;
  @service store;
  @service shData;

  page = 1;
  pagination = null;
  sort = ADMIN_USER_CONTROLLER.SORTS.CREATED_AT;
  sortOrder = ADMIN_USER_CONTROLLER.SORT_ORDERS.DESC;

  get className() {
    return CLASS_NAME;
  }

  get queryParams() {
    return [
      'page',
      'sort',
      'sortOrder',
      ...ADMIN_USER_CONTROLLER.FILTER_VALUES,
    ];
  }

  @computed(...ADMIN_USER_CONTROLLER.FILTER_VALUES)
  get queryValues() {
    const values = this.getProperties(Object.values(ADMIN_USER_CONTROLLER.FILTERS));

    // Re-map boolean values
    Object.keys(values).forEach((key) => {
      if (values[key] === 'false') {
        values[key] = false;
      } else if (values[key] === 'true') {
        values[key] = true;
      }
    })

    return EmberObject.create(values);
  }

  /** @type {number} */
  @computed('pagination.totalPages')
  get paginationTotalPages() {
    return this.pagination?.totalPages ?? 0;
  }

  /** @type {Boolean} */
  @computed('loadUsers.isRunning','paginationTotalPages')
  get showPagination() {
    return !this.loadUsers.isRunning
      && this.paginationTotalPages > 1;
  }

  // TASKS
  @task
  *loadUsers() {
    const {
      sort,
      sortOrder,
      page,
    } = this;

    const filters = this.getProperties(Object.values(ADMIN_USER_CONTROLLER.FILTERS));
    const sortValue = filters[ADMIN_USER_CONTROLLER.FILTERS.NAME]
      ? ADMIN_USER_CONTROLLER.SORTS.BEST_MATCH
      : sort;

    const params = {
      filter: filters,
      include: ADMIN_USER_CONTROLLER.INCLUDES.join(','),
      modelParams: 'applicationMeta',
      sort: sortValue,
      sortOrder,
    };

    params.page = page || 1;

    const users = yield this.store.query('user', params);

    this.set('model.users', users);
    this.set('pagination', this.shData.getPaginationModelFromResponse(users));
    this.set('sort', sortValue);
  }

  resetQueryValues() {
    const filters = Object.values(ADMIN_USER_CONTROLLER.FILTERS).reduce((object, key) => {
      object[key] = undefined;
      return object;
    }, { page: 1 });

    this.setProperties(filters);
  }

  @action
  handlePageChange(increment) {
    const { page } = this;
    this.set('page', page + increment);
  }

  /**
   *
   * @param {Ember.Object} keyValuePairs
   */
  @action
  onSetQueryValues(keyValuePairs) {
    const filters = EmberObject.create(
      Object.values(ADMIN_USER_CONTROLLER.FILTERS).reduce((object, key) => {
        object[key] = undefined;
        return object;
      }, {})
    );

    // Set nulls to undefined or else they'll still be used
    Object.keys(keyValuePairs).map((key) => {
      if (keyValuePairs.get(key) === null) {
        keyValuePairs.set(key, undefined);
      }
    });

    this.set('page', 1);

    filters.setProperties(keyValuePairs);
    this.setProperties(filters);
  }

  @action
  reset() {
    this.set('page', 1);
    this.set('sort', ADMIN_USER_CONTROLLER.SORTS.CREATED_AT);
    this.set('sortOrder', ADMIN_USER_CONTROLLER.SORT_ORDERS.DESC);

    this.resetQueryValues();
  }
}

export default AdminUsersController;
